import { useAuth } from "@clerk/clerk-react";
import { AlertContext } from "contexts/Alert";
import { BundledAddOnItem, CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import { SplashPage } from "contexts/Subscription";
import { useContext } from "react";
import { AddOnItemName, CustomDomainInfo } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { Plans } from "schemas/payments";

import { fetcherAuth } from "utils/api";
import { makeDeepCopy } from "utils/updateLocalState";

import { Coupon, StripeCustomer } from "./schema";

type GeneratePreviewInvoiceArgs = {
  plan?: AddOnItemName | Plans | null;
  organizationId?: number;
  customer?: StripeCustomer;
  coupon?: Coupon;
  addOnItems?: BundledAddOnItem[];
  domainInfo?: CustomDomainInfo | null;
  splashPageSource?: SplashPage | null;
};

export const useCheckout = () => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const {
    domainInfo,
    invoice,
    customer,
    promoCode,
    setPromoCode,
    setNoNeedsToPay,
    addOnItems,
    setInvoice,
    plan,
    source,
    setAddOnItems,
    setApplyAddOnLoading,
    setInvoiceLoading,
    splashPageSource,
  } = useContext(CheckoutContext);

  const { currentOrg } = useContext(OrganizationUserContext);
  const updatePreviewInvoice = async (args: GeneratePreviewInvoiceArgs) => {
    const existingArgs = {
      plan,
      organizationId: currentOrg?.id,
      customer,
      coupon: promoCode,
      addOnItems,
      domainInfo,
      splashPageSource,
    };
    if (args.plan) existingArgs.plan = args.plan;
    if (args.organizationId) existingArgs.organizationId = args.organizationId;
    if (args.customer) existingArgs.customer = args.customer;
    if (args.coupon) existingArgs.coupon = args.coupon;
    if (args.addOnItems) existingArgs.addOnItems = args.addOnItems;
    if (args.domainInfo) existingArgs.domainInfo = args.domainInfo;
    if (args.splashPageSource)
      existingArgs.splashPageSource = args.splashPageSource;

    if (!existingArgs?.plan) return;

    await generatePreviewInvoice(existingArgs);
  };

  const generatePreviewInvoice = async (args: GeneratePreviewInvoiceArgs) => {
    const {
      plan,
      organizationId,
      customer,
      coupon,
      addOnItems,
      domainInfo,
      splashPageSource,
    } = args;
    const isNonRecurringAddOnItem = plan && plan === AddOnItemName.support_call;

    if (isNonRecurringAddOnItem) {
      await generateOneTimeInvoice(plan);
      return;
    }
    const data: Map = {
      plan,
      source,
      stripeCustomerId: customer?.id,
      country: customer?.address?.country,
      postalCode: customer?.address?.postal_code,
      bundledAddOns: addOnItems,
      domainInfo,
      splashPageSource,
    };
    if (coupon?.id) {
      data.promoCode = coupon.id;
    }
    try {
      setInvoiceLoading(true);
      const { invoice } = await fetcherAuth(
        getToken,
        `/api/organization/${organizationId}/payments/subscription/invoice`,
        "POST",
        {},
        data,
      );
      if (invoice?.amount_due === 0) {
        setNoNeedsToPay(true);
      }

      const promoId = invoice?.discounts?.[0]?.promotion_code;
      const promoName = invoice?.discounts?.[0]?.coupon?.name;
      if (promoId && promoName && promoCode?.id !== promoId) {
        setPromoCode({
          id: promoId,
          name: promoName,
        });
      }
      setInvoice(invoice);
    } catch (error) {
      if (error.message.includes("promotion code")) {
        setPromoCode({
          id: "",
          name: "",
        });
        updatePreviewInvoice({
          ...args,
          coupon: {
            id: "",
            name: "",
          },
        });
      }
      setErrorAlert(error);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const generateOneTimeInvoice = async (plan: AddOnItemName) => {
    if (!currentOrg) return;

    setInvoiceLoading(true);
    try {
      const { invoice } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/invoice/create`,
        "POST",
        {},
        {
          plan,
          source,
        },
      );
      setInvoice(invoice);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const applyPromoCode = async (coupon: Coupon) => {
    if (!currentOrg || !plan) return;
    setPromoCode(coupon);
    updatePreviewInvoice({
      coupon,
    });
  };

  const removePromoCode = async () => {
    if (!currentOrg || !plan) return;
    setPromoCode({ id: "", name: "" });
    updatePreviewInvoice({
      coupon: undefined,
    });
  };

  const applyAddOnItem = async (item: BundledAddOnItem) => {
    if (!currentOrg || !plan) return;
    setApplyAddOnLoading(item.name);
    const copy = makeDeepCopy(addOnItems || []);
    const index = copy.findIndex((x: BundledAddOnItem) => x.name === item.name);
    if (item.quantity === 0) {
      if (index !== -1) {
        // If quantity is 0, remove the item from the list
        copy.splice(index, 1);
      }
    } else {
      if (index !== -1) {
        copy[index].quantity = item.quantity;
      } else {
        copy.push(item);
      }
    }
    await updatePreviewInvoice({
      addOnItems: copy,
    });
    setAddOnItems(copy);
    setApplyAddOnLoading(null);
  };

  const getQuantityOfAddOnFromInvoice = (addOnItemName: AddOnItemName) => {
    if (!invoice) return 0;
    return (
      invoice?.lines?.data?.filter(
        (item) => item.metadata?.item_name === addOnItemName,
      )?.[0]?.quantity || 0
    );
  };

  return {
    applyPromoCode,
    removePromoCode,
    applyAddOnItem,
    getQuantityOfAddOnFromInvoice,
    updatePreviewInvoice,
  };
};
